import React, {memo, useEffect} from 'react'
import {startCase} from 'lodash'
import {Button, Form, Header, List, Message, Modal, Dropdown} from 'semantic-ui-react'
import {
  withArchiveData,
  withArchivesData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withServicesData
} from '../../hoc'
import {goBack} from '../../util'
import {restoreArchive} from '../../backend'
import {useSafeState} from '../../hook'

const isServicePresent = (services, name) => {
  return services.some((s) => s.name.toLowerCase() === name.toLowerCase())
}

const getFormattedName = (archive) => {
  if (archive.type === 'SERVICE') {
    return `${archive.serviceName}-restore-${archive.reference}`
  }
  if (archive.type === 'VERSION') {
    return `${archive.versionName}-restore-${archive.reference}`
  }
  if (archive.type === 'ENVIRONMENT') {
    return `${archive.environmentName}-restore-${archive.reference}`
  }
}

const _RestoreArchive = (props) => {
  const {
    services,
    archive,
    mutateArchives,
    mutateServices,
    restoreArchive,
    history,
    navigate,
    basePath = '/c/account/existing-archives'
  } = props
  const [state, setState] = useSafeState({
    selectedServiceId: undefined,
    selectedVersionId: undefined,
    name: ''
  })

  useEffect(() => {
    setState({
      ...state,
      selectedServiceId: undefined,
      selectedVersionId: undefined,
      name: getFormattedName(archive)
    })
  }, [archive, services])

  const getServiceOptions = (services) => {
    return services.map((s) => {
      return {key: s.id, text: s.name, value: s.id}
    })
  }

  const getVersionOptions = (service) => {
    return service.versions.map((v) => {
      return {key: v.id, text: v.name, value: v.id}
    })
  }

  const isSubmitDisabled = (availableVersionCount) => {
    if(archive.type === 'SERVICE') {
      return false
    }
    if (archive.type === 'VERSION') {
      return !state.selectedServiceId
    }
    if (archive.type === 'ENVIRONMENT' && availableVersionCount > 0) {
      return !state.selectedServiceId && !!state.selectedVersionId
    }
    return true
  }

  const onClose = () => {
    goBack(history, navigate, basePath)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setState({...state, submitting: true})
    await restoreArchive(archive.id, state.name, state.selectedVersionId || state.selectedServiceId)
    mutateArchives()
    mutateServices()
    navigate(`${basePath}?restoreSucceeded=${state.name}`)
  }

  const onServiceSelected = (e, d) => {
    setState({
      ...state,
      selectedServiceId: d.value,
      selectedVersionId: d.value !== state.selectedServiceId ? undefined : state.selectedVersionId
    })
  }

  const onVersionSelected = (e, d) => {
    setState({
      ...state,
      selectedVersionId: d.value
    })
  }

  const {submitting} = state
  const serviceOptions = archive.type !== 'SERVICE' ? getServiceOptions(services) : []
  const versionOptions = state.selectedServiceId ? getVersionOptions(services.find(s => s.id === state.selectedServiceId)) : []

  return (
    <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose}
           style={{position: '!important relative'}} data-testid='archive-restore-modal'>
      <Header content={`Restore ${startCase(archive.type.toLowerCase())} Archive`}/>
      <Modal.Content>
        <Form>
          {
            archive.type !== 'SERVICE' && (
              <Form.Field>
                <label>Service</label>
                <Dropdown placeholder='Select service' fluid search selection
                          disabled={serviceOptions.length === 0}
                          options={serviceOptions}
                          value={state.selectedServiceId}
                          data-testid='restore-to-service-dropdown'
                          onChange={onServiceSelected}/>
              </Form.Field>
            )
          }
          {
            (archive.type === 'ENVIRONMENT' && !!state.selectedServiceId && versionOptions.length === 0) && (
              <Message>There are no versions present in the selected service. Please restore or add a version to this
                service and try again.</Message>
            )
          }
          {
            archive.type === 'ENVIRONMENT' && versionOptions.length > 0 && (
              <Form.Field>
                <label>Version</label>
                <Dropdown placeholder='Select version' fluid search selection
                          disabled={versionOptions.length === 0}
                          options={versionOptions}
                          value={state.selectedVersionId}
                          data-testid='restore-to-version-dropdown'
                          onChange={onVersionSelected}/>
              </Form.Field>
            )
          }
          {
            (archive.type !== 'ENVIRONMENT' || archive.type === 'ENVIRONMENT' && versionOptions.length > 0) &&
            (
              <Form.Field>
                <label>Name</label>
                <div>{state.name}</div>
                <Message size='small'>You can rename this after restoring completes.</Message>
              </Form.Field>
            )
          }
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} data-testid='archive-restore-cancel-button'>Cancel</Button>
        <Button primary
                disabled={isSubmitDisabled(versionOptions.length)}
                onClick={onSubmit}
                data-testid='archive-restore-submit-button'
                loading={submitting}>Restore</Button>
      </Modal.Actions>
    </Modal>
  )
}

let RestoreArchive = (props) => {
  const {handleError} = props
  props = {
    ...props,
    history: window.history,
    restoreArchive: handleError(restoreArchive)
  }
  return (<_RestoreArchive {...props}/>)
}

RestoreArchive = withHelmet(RestoreArchive, 'Restore Archive')
RestoreArchive = withPropsPresent(RestoreArchive, ['archive', 'archives', 'services'])
RestoreArchive = withArchiveData(RestoreArchive, 'Loading archive...')
RestoreArchive = withArchivesData(RestoreArchive, 'Loading archives...')
RestoreArchive = withServicesData(RestoreArchive, 'Loading services...')
RestoreArchive = withErrorHandling(RestoreArchive)
RestoreArchive = memo(RestoreArchive)

export {_RestoreArchive, RestoreArchive, isServicePresent}

export default RestoreArchive